import {
    Component,
    computed,
    effect,
    ElementRef,
    forwardRef,
    HostListener,
    inject,
    Injector,
    input,
    OnDestroy,
    signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AclDirective } from 'app/shared/acl/acl-resource.directive';
import { isEqual } from 'app/shared/utils';
import { OverlayRef, OverlaysService, POSITION_TARGET, PositionTarget } from '../overlays';
import { DropdownMenuComponent } from './dropdown-menu.component';
import { Option } from './option.model';
import { SelectBoxOptionComponent } from './select-box-option.component';


@Component({
    selector: 'ui-select-box',
    imports: [
        AclDirective,
        SelectBoxOptionComponent,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectBoxComponent),
            multi: true,
        },
    ],
    styles: [
        `
        :host {
            padding: 2px;
            min-height: calc(2.25rem - 2px);
            height: auto;
            display: flex;
            align-content: center;
            justify-content: space-between;
        }
        .option-box {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            position: relative;
            gap: 2px 4px;
        }
        .placeholder {
            padding: 0 0.75rem;
        }
        /* wait until the last removed option has faded out */
        .option-box:has(.fade-leave) .placeholder {
            display: none;
        }
        .close-dropdown-padding {
            padding: 2px 4px 2px;
        }
        .close-dropdown-button {
            cursor: pointer;
        }
        :host.disabled {
            background-color: #e9ecef;
        }
        `,
    ],
    host: {
        '[attr.tabindex]': 'isDisabled() ? null : 0',
        'class': 'form-control',
        '[class.disabled]': 'isDisabled()',
    },
    template: `
        <div class="option-box">
            @for (option of selectedOptions(); track option.key) {
                <ui-select-box-option
                    *apAcl="option.aclResourceName"
                    [text]="option.title"
                    (remove)="removeOption(option.key)">
                </ui-select-box-option>
            }
            @if (isPlaceholderVisible()) {
                <span class="placeholder">
                    {{ options().length == 0 ? 'No options available' : placeholder() }}
                </span>
            }
        </div>
        @if (selectedOptionKeys().length > 0) {
            <div class="close-dropdown-padding">
                <span class="close-dropdown-button" (click)="clearSelection()">
                    <i class="fa fa-close"></i>
                </span>
            </div>
        }
    `,
})
export class SelectBoxComponent implements ControlValueAccessor, PositionTarget, OnDestroy {

    options = input([], { transform: (value: Option[] | undefined) => value || [] });

    showAllOption = input(true);

    placeholder = input('Select Options...');

    selectedOptionKeys = signal<string[]>([], { equal: isEqual });

    selectedOptions = computed(() => {
        const options = this.options();
        const selectedKeys = this.selectedOptionKeys();
        return selectedKeys.map(key => options.find(o => o.key === key)).filter(option => typeof option !== 'undefined');
    }, { equal: isEqual });

    isPlaceholderVisible = computed(() => this.selectedOptionKeys().length === 0);

    isDisabled = signal(false);

    private clearingClick = false;

    private dropdownMenu: OverlayRef<DropdownMenuComponent> | null;

    private onChange = <T>(_: T) => {};

    private onTouched = () => {};

    private elementRef = inject(ElementRef);

    private overlaysService = inject(OverlaysService);

    private injector = inject(Injector);

    constructor() {
        effect(() => {
            const options = this.options();
            const selectedOptions = this.selectedOptions();

            if (this.dropdownMenu) {
                const { componentRef } = this.dropdownMenu;
                componentRef.setInput('availableOptions', options);
                componentRef.setInput('activeOptions', selectedOptions);
            }
        });

        effect(() => {
            const options = this.options();
            this.selectedOptionKeys.update(keys => keys.filter(key => options.find(o => o.key === key)));
        });

        effect(() => {
            const selectedOptionKeys = this.selectedOptionKeys();
            this.onChange(selectedOptionKeys);
            this.onTouched();
        });
    }

    writeValue(obj: string[]) {
        if (!Array.isArray(obj)) {
            obj = [obj];
        }
        const options = this.options();
        this.selectedOptionKeys.set(obj.filter(key => options.find(o => o.key === key)));
    }

    registerOnChange(fn: () => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        if (this.dropdownMenu && isDisabled) {
            this.dropdownMenu.dismiss('disabled');
        }
        this.isDisabled.set(isDisabled);
    }

    clearSelection() {
        this.selectedOptionKeys.set([]);
        this.clearingClick = true;
    }

    removeOption(optionKey: string) {
        this.selectedOptionKeys.update(keys => keys.filter(key => key !== optionKey));
    }

    private selectOptions(optionKeys: string[]) {
        const availableKeys = this.options().map(option => option.key);
        optionKeys = optionKeys.filter(key => availableKeys.includes(key));

        const selectedKeys = this.selectedOptionKeys().filter(value => optionKeys.includes(value));
        optionKeys = optionKeys.filter(value => !selectedKeys.includes(value));

        this.selectedOptionKeys.set([...selectedKeys, ...optionKeys]);
    }

    private openDropdownMenu() {
        if (this.isDisabled()) {
            return;
        }
        if (this.dropdownMenu) {
            return;
        }
        if (this.options().length === 0) {
            return;
        }
        const injector = Injector.create({
            providers: [{ provide: POSITION_TARGET, useValue: this }],
            parent: this.injector,
        });
        this.dropdownMenu = this.overlaysService.createOverlay(DropdownMenuComponent, {
            inputs: {
                availableOptions: this.options(),
                activeOptions: this.selectedOptions(),
                showAllOption: this.showAllOption(),
            },
            parentInjector: injector,
        });
        this.dropdownMenu.componentRef.instance.selectOptionChange.subscribe((keys: string[]) => {
            this.selectOptions(keys);
        });
        this.dropdownMenu.componentRef.onDestroy(() => {
            this.dropdownMenu = null;
        });
    }

    @HostListener('click')
    onClick() {
        if (this.isDisabled()) {
            return;
        }
        if (this.clearingClick) {
            this.clearingClick = false;
            this.elementRef.nativeElement.blur();
            return;
        }
        if (this.dropdownMenu) {
            this.dropdownMenu.dismiss('toggle');
        } else {
            this.openDropdownMenu();
            if (this.dropdownMenu) {
                (this.dropdownMenu as OverlayRef<DropdownMenuComponent>).componentRef.instance.setFocus();
            }
        }
    }

    onEditableAreaInput() {
        this.openDropdownMenu();
    }

    @HostListener('keydown.enter', ['$event'])
    onEnter(event: Event) {
        if (this.dropdownMenu) {
            event.preventDefault();
            this.dropdownMenu.componentRef.instance.selectOption();
        } else {
            this.openDropdownMenu();
        }
    }

    @HostListener('keydown.tab', ['$event'])
    @HostListener('keydown.shift.tab', ['$event'])
    onTab(event: Event) {
        if (this.dropdownMenu) {
            event.preventDefault();
            this.dropdownMenu.componentRef.instance.selectOption();
        }
    }

    @HostListener('keydown.arrowup', ['$event'])
    onUp(event: Event) {
        event.preventDefault();
        if (this.dropdownMenu) {
            this.dropdownMenu.componentRef.instance.moveUp();
        }
        this.openDropdownMenu();
    }

    @HostListener('keydown.arrowdown', ['$event'])
    onDown(event: Event) {
        event.preventDefault();
        if (this.dropdownMenu) {
            this.dropdownMenu.componentRef.instance.moveDown();
        }
        this.openDropdownMenu();
    }

    getBoundingClientRect() {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }

    ngOnDestroy() {
        if (this.dropdownMenu) {
            this.dropdownMenu.dismiss('remove');
        }
    }
}
